import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import './AboutUs.css';
import FAQSection from './FAQSection';
import { company1, company2, company3, company4 } from '../assets';

const AboutUs = () => {
  const controls = useAnimation();
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  // const [storyRef, storyInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [missionRef, missionInView] = useInView({ threshold: 0.1 });

  const [currentStat, setCurrentStat] = useState(0);

  useEffect(() => {
    if (heroInView) controls.start("visible");
  }, [controls, heroInView]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };


  const stats = [
    { number: "500+", label: "Businesses Transformed", icon: "bi-building" },
    { number: "99.9%", label: "System Uptime", icon: "bi-shield-check" },
    { number: "24/7", label: "US-Based Support", icon: "bi-headset" },
    { number: "15+", label: "Years of Experience", icon: "bi-award" }
  ];

  const services = [
    {
      title: "Cloud ERP Implementation",
      description: "Customized NetSuite ERP and cloud ERP software for American businesses, ensuring seamless integration with your existing tools.",
      icon: "bi-cloud-check",
      gradient: "linear-gradient(135deg, #6c757d, #495057)"
    },
    {
      title: "Digital Transformation Services",
      description: "US-based ERP experts helping companies boost productivity, reduce costs, and scale operations.",
      icon: "bi-arrow-up-right-circle",
      gradient: "linear-gradient(135deg, #495057, #343a40)"
    },
    {
      title: "Ongoing ERP Support",
      description: "Continuous training, updates, and 24/7 ERP system monitoring from a local support team that understands your market.",
      icon: "bi-gear-wide-connected",
      gradient: "linear-gradient(135deg, #343a40, #6c757d)"
    }
  ];

  const benefits = [
    {
      title: "ERP Scalability",
      description: "Grow your ERP capabilities as your business expands, without costly hardware investments.",
      icon: "bi-graph-up-arrow"
    },
    {
      title: "Real-Time Business Analytics",
      description: "Gain instant access to financial data, sales insights, and operational dashboards.",
      icon: "bi-bar-chart-line"
    },
    {
      title: "Enhanced Security & Compliance",
      description: "Secure ERP hosting with advanced encryption, automated updates, and US compliance tools.",
      icon: "bi-shield-lock"
    },
    {
      title: "ERP Automation & Efficiency",
      description: "Replace disconnected systems, automate workflows, and speed up decision-making.",
      icon: "bi-lightning-charge"
    },
    {
      title: "Reliable ERP Support",
      description: "Minimize downtime with 24/7 US-based ERP help desk support.",
      icon: "bi-telephone"
    },
    {
      title: "Cost Optimization",
      description: "Reduce operational expenses with pay-as-you-go cloud ERP hosting, eliminating upfront hardware costs.",
      icon: "bi-cash-coin"
    }
  ];
  

  const values = [
    {
      title: "Customer First",
      description: "Your business success drives every decision we make.",
      icon: "bi-person-heart"
    },
    {
      title: "Transparency",
      description: "Clear pricing, open communication, no hidden costs.",
      icon: "bi-eye"
    },
    {
      title: "Innovation",
      description: "Continuous improvement in ERP features and integrations.",
      icon: "bi-lightbulb"
    },
    {
      title: "Security",
      description: "Protecting your most valuable business asset — your data.",
      icon: "bi-shield-check"
    },
    {
      title: "Excellence",
      description: "Delivering ERP solutions that exceed expectations.",
      icon: "bi-star"
    }
  ];

  return (
    <div className="about-page bg-white overflow-hidden">
      <Helmet>
        <title>About SpireNest | NetSuite Experts USA</title>
        <link rel="canonical" href="https://spirenest.co/about" />
        <meta name="description" content="Learn about SpireNest's commitment to delivering top-tier NetSuite services, helping U.S. businesses achieve operational excellence." />
        <meta name="keywords" content="SpireNest, NetSuite partner, ERP solutions, CRM solutions, USA, NetSuite experts" />
        <meta property="og:title" content="About SpireNest | USA-Based NetSuite ERP Consulting Company" />
        <meta property="og:description" content="Learn how SpireNest helps US companies grow with reliable NetSuite services tailored for success." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/about-us" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-about.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About SpireNest | USA-Based NetSuite ERP Consulting Company" />
        <meta name="twitter:description" content="We help companies scale with smarter NetSuite implementations and ERP support." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-about.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": "https://spirenest.co/about-us",
                "name": "About SpireNest | USA-Based NetSuite ERP Consulting Company",
                "description": "Learn how SpireNest helps US companies grow with reliable NetSuite services tailored for success.",
                "url": "https://spirenest.co/about-us",
                "image": "https://spirenest.co/assets/images/og-about.jpg",
                "publisher": {
                  "@type": "Organization",
                  "name": "SpireNest",
                  "url": "https://spirenest.co/",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://spirenest.co/assets/logo-Cko-LaIl.png"
                  }
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://spirenest.co/about-us#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://spirenest.co/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "About Us",
                    "item": "https://spirenest.co/about-us"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="about-hero position-relative" style={{minHeight: '93vh'}}>
        <div className="about-hero-bg"></div>
        
        <Container className="position-relative h-100 d-flex align-items-center mt-4">
          <Row className="w-100 align-items-center">
            <Col lg={7}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
              >
                <motion.span variants={slideInLeft} className="hero-badge">
                  TRUSTED CLOUD ERP SOLUTIONS
                </motion.span>
                <motion.h1 variants={slideInLeft} className="fw-bold display-2 mb-4 text-white hero-title">
                  Trusted Cloud ERP Solutions for <span style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Businesses </span>
                </motion.h1>
                <motion.p variants={slideInLeft} className="lead text-white-75 mb-5 hero-description">
                  SpireNest is your trusted partner for cloud ERP solutions in the USA, helping businesses operate smarter, faster, and more efficiently. We specialize in cloud-based Enterprise Resource Planning (ERP) to streamline finance management, inventory control, human resources, supply chain, and operations all within one powerful, secure ERP platform.
                </motion.p>
                <motion.div variants={slideInLeft}>
                  <motion.div
                    className="btn btn-light py-3 rounded-pill fw-semibold me-3 mb-5"
                    whileHover={{ scale: 1.05, boxShadow: "0 15px 35px rgba(21, 101, 192, 0.4)" }}
                    whileTap={{ scale: 0.95 }}
                    style={{ cursor: 'pointer' }}
                    onClick={() => window.location.href = '/contact'}
                  >
                    Get Started with NetSuite
                  </motion.div>
                </motion.div>

              </motion.div>
            </Col>
            <Col lg={5}>
              <motion.div
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={slideInRight}
                className="stats-container"
              >
                <div className="animated-stats">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={index}
                      className={`stat-item ${currentStat === index ? 'active' : ''}`}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: currentStat === index ? 1.1 : 1, opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="stat-icon">
                        <i className={`${stat.icon}`} style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: '2rem'}}></i>
                      </div>
                      <div className="stat-content">
                        <h3 className="stat-number text-white">{stat.number}</h3>
                        <p className="stat-label text-white-75">{stat.label}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Story Section */}
     

      {/* Services Section */}
      <section className="services-section py-5 bg-light">
        <Container>
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              {/* <span className="section-badge">WHAT WE DO</span> */}
              <h2 className="fw-bold display-5 mb-3 section-title">Our Services</h2>
              <p className="lead text-muted max-width-600">
                Comprehensive ERP solutions designed for American businesses
              </p>
            </motion.div>
            
            <Row className="g-4">
              {services.map((service, index) => (
                <Col lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                    whileHover={{ y: -10, transition: { duration: 0.3 } }}
                    className="service-card h-100"
                  >
                    <div className="service-header bg-dark" >
                      <div className="service-icon">
                        <i className={`${service.icon} text-white`}></i>
                      </div>
                    </div>
                    <div className="service-body">
                      <h5 className="fw-bold mb-3">{service.title}</h5>
                      <p className="text-muted mb-0">{service.description}</p>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              {/* <span className="section-badge">KEY BENEFITS</span> */}
              <h2 className="fw-bold display-5 mb-3 section-title">Key Benefits of SpireNest ERP Solutions</h2>
              <p className="lead text-muted max-width-800">
                Experience the advantages that set our ERP solutions apart
              </p>
            </motion.div>
            
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  whileHover={{ scale: 1.05 }}
                  className="benefit-item"
                >
                  <div className="benefit-icon">
                    <i className={`${benefit.icon} text-primary`}></i>
                  </div>
                  <div className="benefit-content">
                    <h6 className="fw-bold mb-2">{benefit.title}</h6>
                    <p className="text-muted small mb-0">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Mission & Vision Section */}
      <section className="mission-vision-section py-5 bg-dark text-white">
        <Container>
          <motion.div
            ref={missionRef}
            initial="hidden"
            animate={missionInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row>
              <Col lg={6}>
                <motion.div variants={slideInLeft} className="mission-card">
                <div className="mission-icon mb-4">
                  <i className="bi-rocket" style={{ fontSize: '3rem', background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}></i>
                </div>
                <h3 className="fw-bold mb-4">Our Mission</h3>

                  <p className="mb-0">
                    To empower US businesses to transform, compete, and thrive by delivering secure, scalable, and easy-to-use ERP technology.
                  </p>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div variants={slideInRight} className="vision-card">
                  <div className="vision-icon mb-4">
                    <i className="bi-eye" style={{fontSize: '3rem', background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}></i>
                  </div>
                  <h3 className="fw-bold mb-4">Our Vision</h3>
                  <p className="mb-0">
                    A future where every American business — from startups to enterprises — runs on a unified ERP system for clarity, efficiency, and growth.
                  </p>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Values Section */}
      <section className="values-section py-5 bg-light">
        <Container>
          <motion.div
            ref={valuesRef}
            initial="hidden"
            animate={valuesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <span className="section-badge">OUR PRINCIPLES</span>
              <h2 className="fw-bold display-5 mb-3 section-title">Our Core Values</h2>
              <p className="lead text-muted max-width-600">
                The principles that guide everything we do
              </p>
            </motion.div>
            
            <Row className="g-4">
              {values.map((value, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeIn}
                    whileHover={{ y: -10, transition: { duration: 0.3 } }}
                  >
                    <Card className="h-100 border-0 shadow-sm value-card bg-white">
                      <Card.Body className="text-center p-4">
                        <div className="value-icon mx-auto mb-3">
                          <i className={`${value.icon} fs-2 text-primary`}></i>
                        </div>
                        <h5 className="fw-bold mb-3 text-black">{value.title}</h5>
                        <p className="text-muted mb-0">{value.description}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section py-5">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <span className="section-badge">WHY CHOOSE US</span>
              <h2 className="fw-bold display-5 mb-3 section-title">Why Choose SpireNest?</h2>
            </motion.div>
            
            <Row className="align-items-center">
              <Col lg={6}>
                <motion.div variants={slideInLeft}>
                  <div className="why-choose-list">
                    <div className="why-choose-item">
                      <div className="why-icon">
                        <i className="bi-award text-primary"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-2">Industry-leading ERP implementation experts in the USA</h6>
                        <p className="text-muted small">Proven track record with 500+ successful implementations</p>
                      </div>
                    </div>
                    <div className="why-choose-item">
                      <div className="why-icon">
                        <i className="bi-person-gear text-primary"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-2">Personalized ERP services for startups, SMBs, and enterprises</h6>
                        <p className="text-muted small">Tailored solutions for every business size and industry</p>
                      </div>
                    </div>
                    <div className="why-choose-item">
                      <div className="why-icon">
                        <i className="bi-graph-up text-primary"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-2">Scalable ERP deployment with real-time analytics</h6>
                        <p className="text-muted small">Grow your system capabilities as your business expands</p>
                      </div>
                    </div>
                    <div className="why-choose-item">
                      <div className="why-icon">
                        <i className="bi-lightning text-primary"></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-2">Fast ERP onboarding with local US-based support</h6>
                        <p className="text-muted small">Quick implementation with 24/7 local support team</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div variants={slideInRight} className="text-center">
                  <div className="why-choose-visual">
                    <div className="pulse-circle">
                      <i className="bi-shield-check text-white" style={{fontSize: '4rem'}}></i>
                    </div>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Promise Section */}
      <section className="promise-section py-5  text-white">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div variants={fadeIn}>
              <span className="section-badge-light mb-3">OUR PROMISE</span>
              <h2 className="fw-bold display-5 mb-4 text-white">Our Promise to You</h2>
              <p className="lead mb-4 max-width-800">
                At SpireNest, we make cloud ERP implementation simple. Whether you're upgrading from legacy ERP systems or moving to the cloud for the first time, we guide you from planning to go-live and beyond.
              </p>
              <div className="promise-tagline">
                <h4 className="fw-bold mb-4">SpireNest — Simplifying cloud ERP for American businesses.</h4>
                <div className="tagline-features">
                  <span className="feature-tag">Unified</span>
                  <span className="feature-tag">Scalable</span>
                  <span className="feature-tag">Secure</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Trusted Clients Section */}
      <section className="trusted-clients-section py-5 bg-white">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <Row className="justify-content-center mb-5">
              <Col lg={8} className="text-center">
                <motion.div variants={fadeIn} className="mb-4">
                  <h3 className="trusted-clients-title mb-3">
                    Over 50 clients trust us for NetSuite expertise
                  </h3>
                  <p className="trusted-clients-subtitle">
                    Partner with us to unlock your business potential
                  </p>
                </motion.div>
              </Col>
            </Row>
            
            <Row className="g-4 mb-5">
              {/* Partner Company Cards */}
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeIn}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company1} 
                          alt="Company 1 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeIn}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company2} 
                          alt="Company 2 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeIn}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company3}
                          alt="Company 3 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6} sm={6}>
                <motion.div 
                  variants={fadeIn}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="partner-card"
                >
                  <Card className="partner-company-card border-0 shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center p-4">
                      <div className="partner-logo-container">
                        <img 
                          src={company4}
                          alt="Company 4 Logo" 
                          className="partner-logo"
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            </Row>
            
            {/* CTA Button */}
            <Row className="justify-content-center">
              <Col lg={6} className="text-center">
                <motion.div variants={fadeIn}>
                  <Button
                    className="btn-cta-custom btn-lg px-5 py-3"
                    onClick={() => window.location.href = '/contact'}
                  >
                    <i className="bi-rocket me-2"></i>
                    Start Your NetSuite Journey
                  </Button>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <FAQSection />
    </div>
  );
};

export default AboutUs;